import type { CSSProperties } from 'react';

/**
 * BizRnR logo — the cyan chat-bubble mark with "ai" glyph + sparkle.
 * Ported verbatim from docs/design/mockups/full-v4-final.html (`.biz-bubble`, line 1942).
 *
 * Use `<BizRnRLogo />` for the icon alone; pair with a separate wordmark
 * `<span>` in the nav/footer so the wordmark wrapping is controlled by layout.
 *
 * Colour defaults to `var(--cyan-glow)` with drop-shadow glow to match the
 * mockup's signature look. Override via `color` prop for dark/light surfaces.
 */
export function BizRnRLogo({
  size = 32,
  color = 'var(--cyan-glow)',
  glow = true,
  title = 'BizRnR',
  style,
}: {
  size?: number;
  color?: string;
  glow?: boolean;
  title?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 46"
      fill="none"
      role="img"
      aria-label={title}
      style={{
        color,
        filter: glow ? 'drop-shadow(0 0 10px currentColor)' : undefined,
        flexShrink: 0,
        ...style,
      }}
    >
      <title>{title}</title>
      {/* Chat bubble outline */}
      <path
        d="M24 4C12 4 4 12 4 22c0 5.5 2.8 10.5 7.3 13.3L9.6 43l8.5-4c1.9.6 3.8.9 5.9.9 12 0 20-8 20-18S36 4 24 4z"
        stroke="currentColor"
        strokeWidth="2.2"
        fill="none"
        strokeLinejoin="round"
      />
      {/* "ai" wordmark inside bubble */}
      <text
        x="24"
        y="28"
        textAnchor="middle"
        fontFamily="var(--font-geist-sans), Geist, sans-serif"
        fontSize="14"
        fontWeight="700"
        fill="currentColor"
        letterSpacing="-0.04em"
      >
        ai
      </text>
      {/* Sparkle */}
      <path
        d="M36 10l1 2.2L39.2 13 37 14l-1 2.2-1-2.2L32.8 13 35 12z"
        fill="currentColor"
      />
    </svg>
  );
}

export default BizRnRLogo;
